import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useCommands, useRedirectToMainApp, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { ThankYouStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';
import { getSelectedExpert } from '@packages/shared/core/experts/store';
import { useChat, useCreateChat } from '@packages/shared/core/chat/hooks';
import { stringifyQueryParams } from '@packages/shared/core/common/utils/query';
import {
  SimpleRegChatOnFunnel2ExperimentGroups,
  SimpleRegChatOnFunnel2ExperimentName,
} from '@packages/shared/core/feature-flags/config';

type ThankYouStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const ThankYouStep = ({ id, renderQuizScreen }: ThankYouStepProps) => {
  const { logger, chatService, featureFlags } = useServices();

  const { redirectToSignupFreeTrialPage, redirectToExpertsPage } = useRedirectToMainApp();
  const { back, stepData, userPreferences } = useQuizStep(id);
  const { createChat } = useCreateChat();

  const expert = useSelector(getSelectedExpert);

  const { createCommand } = useCommands();

  const experiment = featureFlags.getExperimentGroup(SimpleRegChatOnFunnel2ExperimentName);

  const isExperiment =
    experiment === SimpleRegChatOnFunnel2ExperimentGroups.EXPERTS ||
    experiment === SimpleRegChatOnFunnel2ExperimentGroups.ONLY_MESSAGE;

  const expertId = expert ? expert.id : '';
  const { outgoingMessage } = useChat(expertId);

  if (!stepData) {
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const goBack = async () => back();

  const goNext = async () => {
    if (isExperiment) {
      await redirectToChatWithMessage();
      return;
    }

    await redirectToSignupFreeTrialPage();
  };

  const redirectToChatWithMessage = async () => {
    if (!expert) {
      redirectToExpertsPage();
      return;
    }

    const userMessage = userPreferences.askAnExpert || outgoingMessage;

    const { chatId } = await chatService.createChat(expert.id);

    const commandParams = stringifyQueryParams({
      chatId,
      text: userMessage,
    });
    await createCommand(`sendMessage?${commandParams}`);
  };

  const redirectToChat = async () => {
    if (expert && !isExperiment) {
      await createChat(expert.id);
      return;
    }

    await goNext();
  };

  return (
    <>
      {renderQuizScreen({
        onContinue: goNext,
        onBack: goBack,
        redirectToChat,
        ...(stepData as ThankYouStepData),
      })}
    </>
  );
};

export default ThankYouStep;
